import React, { useCallback, useEffect, useState } from 'react';
import FormInput from '../../common/FormInput/FormInput';
import FormSelect from '../../common/FormSelect/FormSelect';
import Loading from '../../common/Loading/Loading';
import {
  PaymentGatewayService,
  type PortalPaymentAccountSearchResult,
} from '../../../services/PaymentGatewayService';
import { PAYMENT_SYSTEM_LABELS, type PaymentSystem } from '../../../types';
import { formatDate } from '../../../utils';
import { useEscapeToClose } from '../../../hooks/useEscapeToClose';
import styles from './LinkPaymentAccountModal.module.css';

const PAYMENT_SYSTEM_OPTIONS = [
  { value: '', label: 'Todos os sistemas' },
  { value: 'asaas', label: 'Asaas' },
  { value: 'stripe', label: 'Stripe' },
  { value: 'paypal', label: 'PayPal' },
] as const;

interface LinkPaymentAccountModalProps {
  open: boolean;
  loading: boolean;
  studentId: number;
  currentPaymentAccountId?: number | null;
  title?: string;
  message?: string;
  confirmLabel?: string;
  onConfirm: (account: PortalPaymentAccountSearchResult) => void;
  onCancel: () => void;
}

const LinkPaymentAccountModal: React.FC<LinkPaymentAccountModalProps> = ({
  open,
  loading,
  studentId,
  currentPaymentAccountId = null,
  title = 'Vincular conta de pagamento',
  message = 'Busque uma conta com assinatura ativa no portal para compartilhar o mesmo cliente e a mesma assinatura (ex.: mãe e filha). Se a aluna já tiver a conta vinculada, confirme para entrar na assinatura ativa.',
  confirmLabel = 'Vincular conta',
  onConfirm,
  onCancel,
}) => {
  const [search, setSearch] = useState('');
  const [paymentSystem, setPaymentSystem] = useState('');
  const [results, setResults] = useState<PortalPaymentAccountSearchResult[]>([]);
  const [selectedAccountId, setSelectedAccountId] = useState<number | null>(null);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);

  const resetState = useCallback(() => {
    setSearch('');
    setPaymentSystem('');
    setResults([]);
    setSelectedAccountId(null);
    setSearching(false);
    setSearchError(null);
  }, []);

  useEffect(() => {
    if (!open) {
      resetState();
    }
  }, [open, resetState]);

  useEscapeToClose(onCancel, open && !loading && !searching);

  const runSearch = useCallback(async () => {
    setSearching(true);
    setSearchError(null);
    setSelectedAccountId(null);

    try {
      const data = await PaymentGatewayService.searchPortalPaymentAccounts(
        studentId,
        search.trim() || undefined,
        paymentSystem ? paymentSystem as PaymentSystem : undefined,
      );
      setResults(data);
    } catch {
      setResults([]);
      setSearchError('Não foi possível buscar contas de pagamento.');
    } finally {
      setSearching(false);
    }
  }, [paymentSystem, search, studentId]);

  useEffect(() => {
    if (!open) return;

    const timer = window.setTimeout(() => {
      void runSearch();
    }, 300);

    return () => window.clearTimeout(timer);
  }, [open, runSearch]);

  if (!open) return null;

  const selectedAccount = results.find((account) => account.id === selectedAccountId) ?? null;

  return (
    <div className={styles.overlay}>
      <div className={styles.modal} onClick={(event) => event.stopPropagation()}>
        <div className={styles['icon-circle']}>
          <span className="material-symbols-outlined">link</span>
        </div>

        <h3 className={styles.title}>{title}</h3>
        <p className={styles.message}>{message}</p>

        <div className={styles.filters}>
          <FormInput
            id="link-payment-account-search"
            label="Buscar"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Nome, e-mail ou ID externo"
            disabled={loading}
            containerClassName={styles['search-field']}
          />
          <FormSelect
            id="link-payment-account-system"
            label="Sistema"
            options={PAYMENT_SYSTEM_OPTIONS}
            value={paymentSystem}
            onChange={(event) => setPaymentSystem(event.target.value)}
            disabled={loading}
            containerClassName={styles['system-field']}
          />
        </div>

        <div className={styles.results}>
          {searching ? (
            <Loading />
          ) : searchError ? (
            <p className={styles.error}>{searchError}</p>
          ) : results.length === 0 ? (
            <p className={styles.empty}>Nenhuma conta com assinatura ativa encontrada.</p>
          ) : (
            <div className={styles.list}>
              {results.map((account) => {
                const isSelected = selectedAccountId === account.id;
                const isCurrentAccount = currentPaymentAccountId === account.id;
                const isFullyLinked = account.already_linked_to_student
                  && account.shares_active_subscription;
                const isDisabled = loading || isFullyLinked || isCurrentAccount;
                const linkedStudentsLabel = account.linked_students.length > 0
                  ? account.linked_students
                    .map((student) => student.full_name ?? `Aluna #${student.id}`)
                    .join(', ')
                  : 'Sem alunas vinculadas';

                return (
                  <button
                    key={account.id}
                    type="button"
                    className={`${styles.resultItem} ${isSelected ? styles.resultItemSelected : ''}`.trim()}
                    disabled={isDisabled}
                    onClick={() => setSelectedAccountId(account.id)}
                  >
                    <div className={styles.resultHeader}>
                      <span className={styles.resultSystem}>
                        {PAYMENT_SYSTEM_LABELS[account.payment_system as PaymentSystem] ?? account.payment_system}
                      </span>
                      {isCurrentAccount && (
                        <span className={styles.badge}>Conta atual</span>
                      )}
                      {!isCurrentAccount && isFullyLinked && (
                        <span className={styles.badge}>Já vinculada</span>
                      )}
                      {!isCurrentAccount && account.already_linked_to_student && !account.shares_active_subscription && (
                        <span className={styles.badge}>Vincular à assinatura ativa</span>
                      )}
                    </div>
                    <div className={styles.resultLine}>
                      <strong>{account.name ?? 'Sem nome'}</strong>
                    </div>
                    <div className={styles.resultLine}>{account.email ?? '—'}</div>
                    <div className={styles.resultLineMono}>{account.external_customer_id}</div>
                    <div className={styles.resultMeta}>
                      Vinculada a: {linkedStudentsLabel}
                    </div>
                    <div className={styles.resultMeta}>
                      Criada em {formatDate(account.created_at)}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div className={styles.actions}>
          <button
            type="button"
            className={`${styles.btn} ${styles['btn-cancel']}`}
            onClick={onCancel}
            disabled={loading}
          >
            Cancelar
          </button>
          <button
            type="button"
            className={`${styles.btn} ${styles['btn-confirm']}`}
            onClick={() => {
              if (selectedAccount) {
                onConfirm(selectedAccount);
              }
            }}
            disabled={
              loading
              || !selectedAccount
              || (selectedAccount.already_linked_to_student && selectedAccount.shares_active_subscription)
            }
          >
            {loading ? 'Aguarde...' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

export default LinkPaymentAccountModal;
